import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { useId, useState } from "react";

import {
	SECTION_SPEC_LIFECYCLE,
	type SectionSpecVersion,
} from "@/shared/models/SectionSpec";
import {
	compareVersions,
	formatEffectiveDate,
	formatVersionLabel,
} from "../../models/SectionSpecFormat";

const chipColor = {
	[SECTION_SPEC_LIFECYCLE.DRAFT]: "warning",
	[SECTION_SPEC_LIFECYCLE.SCHEDULED]: "info",
	[SECTION_SPEC_LIFECYCLE.CURRENT]: "success",
	[SECTION_SPEC_LIFECYCLE.PUBLISHED]: "default",
} as const;

const textColor = {
	[SECTION_SPEC_LIFECYCLE.DRAFT]: "warning.dark",
	[SECTION_SPEC_LIFECYCLE.SCHEDULED]: "info.dark",
	[SECTION_SPEC_LIFECYCLE.CURRENT]: "success.dark",
	[SECTION_SPEC_LIFECYCLE.PUBLISHED]: "text.primary",
} as const;

const VersionChip = ({ version }: { version: SectionSpecVersion }) => (
	<Chip
		size="small"
		color={chipColor[version.LifecycleCode]}
		label={formatVersionLabel(version)}
		variant={
			version.LifecycleCode === SECTION_SPEC_LIFECYCLE.PUBLISHED
				? "outlined"
				: "filled"
		}
	/>
);

interface SectionSpecRevisionSelectProps {
	versions: SectionSpecVersion[];
	selected: SectionSpecVersion;
	disabled: boolean;
	loading: boolean;
	onSelect: (profileSpecId: number) => void;
}

export const SectionSpecRevisionSelect = ({
	versions,
	selected,
	disabled,
	loading,
	onSelect,
}: SectionSpecRevisionSelectProps) => {
	const chipId = useId();
	const [search, setSearch] = useState("");

	if (versions.length === 1) return <VersionChip version={selected} />;

	return (
		<Autocomplete
			disableClearable
			disabled={disabled}
			loading={loading}
			options={versions.slice().sort(compareVersions)}
			value={selected}
			inputValue={search}
			onInputChange={(_, value, reason) =>
				setSearch(reason === "input" ? value : "")
			}
			onBlur={() => setSearch("")}
			getOptionLabel={formatVersionLabel}
			isOptionEqualToValue={(option, value) =>
				option.ProfileSpecId === value.ProfileSpecId
			}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props as typeof props & {
					key: string;
				};
				return (
					<li key={key} {...optionProps}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
							<VersionChip version={option} />
							{option.LifecycleCode !== SECTION_SPEC_LIFECYCLE.DRAFT && (
								<Typography variant="caption" color="text.secondary">
									{formatEffectiveDate(option.EffectiveDate)}
								</Typography>
							)}
						</Box>
					</li>
				);
			}}
			onChange={(_, option) => {
				setSearch("");
				onSelect(option.ProfileSpecId);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Revision"
					size="small"
					inputProps={{ ...params.inputProps, "aria-describedby": chipId }}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<>
								{params.InputProps.startAdornment}
								<Box id={chipId} sx={{ display: "flex", mr: 0.5 }}>
									<VersionChip version={selected} />
								</Box>
							</>
						),
					}}
					sx={{
						"& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
							color: textColor[selected.LifecycleCode],
							fontWeight: 600,
							minWidth: "10ch",
						},
					}}
				/>
			)}
			sx={{ flexShrink: 0, width: 250 }}
		/>
	);
};
